import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Bookmark, ChevronLeft, ChevronRight, Play, Check, Clock, Settings, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import Latex from '../../../../../apps/frontend/src/components/global/Latex';
import { QUESTION_DIFFICULTY } from '../../utils/Question_Difficulty';
import YoutubeVideoSolution from '../solution/VideoSolution';
import { getOption, getNumericalOption } from '../solution/Options';
import BookmarkController from '../../MyMistakes/Components/BookmarkController';
import axios from 'axios';
import { QUESTION_TYPE } from '../../utils/Question_Type';
import VideoCipherPlayer from '../solution/VideoCipherPlayer';

const statusStyles = {
  wasted: { color: 'var(--q3-danger-normal)', borderColor: 'var(--q3-danger-light)' },
  confused: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-stroke-normal)' },
  overtime: { color: 'var(--q3-yellow-normal)', borderColor: 'var(--q3-yellow-light)' },
  perfect: { color: 'var(--q3-success-normal)', borderColor: 'var(--q3-success-light)' },
  default: { color: 'var(--q3-neutral-default)', borderColor: 'var(--q3-neutral-default)' },
};

const getStatusStyles = (status) => {
  return statusStyles[status?.toLowerCase()] || statusStyles.default;
};

const getDifficultyStyle = (level) => {
  const styles = {
    3: { color: 'var(--q3-danger-normal)' },
    2: { color: 'var(--q3-yellow-normal)' },
    1: { color: 'var(--q3-success-normal)' },
  };
  return styles[level] || {};
};

export default function NotebookQuestionDrawer({
  isOpen,
  onClose,
  questions,
  currentQuestionIndex,
  onQuestionChange,
  testId,
  packId,
  autoStartTimer,
  onAutoStartTimerChange,
}) {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [time, setTime] = useState(0);
  const [timerState, setTimerState] = useState('initial');
  const [inputValue, setInputValue] = useState('');

  const solutionRef = useRef(null);
  const currentQuestion = questions[currentQuestionIndex];
  const tagDetail = currentQuestion?.user_question_tags?.tags_detail;

  const user = JSON.parse(localStorage.getItem('userProfile'));

  // Reset all states immediately when question changes or drawer opens
  useEffect(() => {
    // Immediately reset all states when question changes
    setSelectedOptions([]);
    setShowSolution(false);
    setIsAnswered(false);
    setTime(0);
    setInputValue('');

    if (isOpen) {
      setIsRendered(true);

      if (autoStartTimer) {
        setTimerState('running');
      } else {
        setTimerState('initial');
      }

      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      setTimerState('stopped');
    }
  }, [isOpen, currentQuestionIndex, autoStartTimer]); // Added currentQuestionIndex to dependencies

  useEffect(() => {
    let interval = null;
    if (timerState === 'running') {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerState]);

  const handleOptionSelect = (optionId) => {
    if (currentQuestion.type === 'multipleCorrect') {
      setSelectedOptions((prev) =>
        prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
      );
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsRendered(false);
    }
  };

  const handleStartTimer = () => {
    setTimerState('running');
  };

  const handleAutoStartToggle = async (newSetting) => {
    onAutoStartTimerChange(newSetting);
    await axios.patch(`/api/user/profile/settings`, { toggleQuestionTimer: newSetting });
    if (newSetting && timerState === 'initial') {
      handleStartTimer();
    }
  };

  const handleViewSolution = () => {
    setShowSolution(true);
    setTimeout(() => {
      solutionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCheckAnswer = () => {
    if (selectedOptions.length > 0 || (currentQuestion?.type === 'numerical' && inputValue)) {
      setIsAnswered(true);
      setTimerState('stopped');
    }
  };

  // Modified navigation handlers to ensure immediate state reset
  const handleNext = () => {
    // Reset states immediately before navigation
    setSelectedOptions([]);
    setShowSolution(false);
    setIsAnswered(false);
    setTime(0);
    setInputValue('');

    onQuestionChange(currentQuestionIndex + 1);
  };

  const handlePrevious = () => {
    // Reset states immediately before navigation
    setSelectedOptions([]);
    setShowSolution(false);
    setIsAnswered(false);
    setTime(0);
    setInputValue('');

    onQuestionChange(currentQuestionIndex - 1);
  };

  const formatTimeTaken = (seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return '00M:00S';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}M:${String(secs).padStart(2, '0')}S`;
  };

  const formatLiveTimer = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  };

  const currentSolution = useMemo(() => {
    const userInputValue = inputValue || currentQuestion?.inputValue;
    const correctValue = currentQuestion?.correctValue;

    // Handle numerical answer correctness with proper number conversion
    let isNumericalCorrect = false;
    if (currentQuestion?.type === 'numerical' && userInputValue && correctValue) {
      const userNum = parseFloat(userInputValue);
      const correctNum = parseFloat(correctValue);

      if (!isNaN(userNum) && !isNaN(correctNum)) {
        // Check if there are numerical limits for tolerance
        const lowerLimit = currentQuestion?.numericalLowerLimit;
        const upperLimit = currentQuestion?.numericalUpperLimit;

        if (lowerLimit && upperLimit) {
          // Use the range if available
          isNumericalCorrect = userNum >= lowerLimit && userNum <= upperLimit;
        } else {
          isNumericalCorrect = Number(userNum) === Number(correctNum);
        }
      }
    }

    return {
      status: isAnswered ? 'answered' : 'not-answered',
      markedOptions: selectedOptions.map((id) => ({ optionId: id })),
      inputValue: isAnswered
        ? {
            value: userInputValue,
            isCorrect: currentQuestion?.type === 'numerical' ? isNumericalCorrect : undefined,
          }
        : userInputValue,
      questionId: currentQuestion,
    };
  }, [isAnswered, selectedOptions, currentQuestion, inputValue]);

  const optionsBefore = useMemo(() => {
    if (currentQuestion.type === 'numerical') {
      return null;
    }

    const options = currentQuestion?.options || currentQuestion?.rawData?.question?.options || [];
    if (!Array.isArray(options) || options.length === 0) return null;

    return options.map((option, index) => {
      const isSelected = selectedOptions.includes(option.id);
      return (
        <div key={option.id} className="h-full cursor-pointer" onClick={() => handleOptionSelect(option.id)}>
          <div className="w-full mb-3">
            <div className="relative">
              <div
                className={`h-full mb-0 rounded-[12px] border ${
                  isSelected ? 'border-[var(--q3-accent-normal)]' : 'bg-[var(--q3-surface-dim)] border-transparent'
                }`}
              >
                <div className="px-4 py-3 flex items-center h-full">
                  <div className="flex items-center w-full">
                    <div className="mr-4 flex-shrink-0">
                      <div className="w-10 h-10 rounded-full flex items-center text-center justify-center text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)]">
                        {String.fromCharCode('A'.charCodeAt(0) + index)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="overflow-x-auto overflow-y-hidden max-w-full scrollbar-thin scrollbar-thumb-[var(--q3-stroke-normal)] scrollbar-track-transparent">
                        {option.imageUrl ? (
                          <img src={option.imageUrl} alt={`Option ${index + 1}`} className="max-h-20 max-w-full" />
                        ) : (
                          <div className="max-w-full">
                            <Latex>{option.text}</Latex>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [currentQuestion, selectedOptions]);

  const optionsAfter = useMemo(() => {
    // Don't render options for numerical questions
    if (currentQuestion.type === 'numerical') {
      return null;
    }

    const options = currentQuestion?.options || currentQuestion?.rawData?.question?.options || [];
    if (!Array.isArray(options) || options.length === 0) return null;

    if (!isAnswered) return null;

    return options.map((option, index) => (
      <div key={option.id} className="h-full">
        {getOption(currentSolution, option, index)}
      </div>
    ));
  }, [currentQuestion, currentSolution, isAnswered]);

  if (!isRendered || !currentQuestion) return null;

  const statusStyle = getStatusStyles(currentQuestion.status);
  const difficultyStyle = getDifficultyStyle(currentQuestion.level);

  const questionText = currentQuestion.question || 'Question text not available';
  const questionOptions = currentQuestion.options || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-700 ease-in-out ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      <div
        className={`relative flex flex-col h-full bg-[var(--q3-surface-default)] shadow-xl w-full lg:w-[60vw] transition-transform duration-700 ease-in-out transform ${isAnimating ? 'translate-x-0' : 'translate-x-full'}`}
        onTransitionEnd={handleAnimationEnd}
      >
        <Button
          onClick={onClose}
          size="icon"
          className="hidden lg:inline-flex rounded-full absolute top-5 -left-10 w-8 h-8 bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dim)]"
        >
          <X size={24} className="text-[var(--q3-neutral-default)]" />
        </Button>
        <div className="border-b border-[var(--q3-stroke-normal)] p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
              Qs {currentQuestionIndex + 1} • {QUESTION_TYPE[currentQuestion.type]}
            </span>
          </div>

          <div className="flex items-center justify-center w-40 h-10 sm:pr-0 pr-8 sm:pl-0">
            {timerState === 'initial' && (
              <Button
                variant="outline"
                className="rounded-full border-[var(--q3-accent-normal)] text-[var(--q3-accent-normal)] hover:text-[var(--q3-accent-normal)] hover:bg-blue-50/50 px-4 py-1 h-auto"
                onClick={handleStartTimer}
              >
                <Clock size={16} className="mr-2" />
                Start Timer
              </Button>
            )}
            {(timerState === 'running' || timerState === 'stopped') && (
              <div className="rounded-full border border-dashed border-gray-300 px-3 py-1 flex items-center gap-2">
                <Clock size={16} className="text-[var(--q3-accent-normal)]" />
                <span className="text-base font-medium text-[var(--q3-accent-normal)] tabular-nums w-20">
                  {formatLiveTimer(time)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <BookmarkController questionId={currentQuestion?.questionId} packId={packId} testId={testId} />

            <Dialog>
              <DialogTrigger asChild>
                <Button
                  size="icon"
                  className="rounded-full w-8 h-8 bg-transparent hover:bg-[var(--q3-surface-dim)] items-center justify-center border border-[var(--q3-stroke-normal)]"
                >
                  <Settings size={18} className="text-[var(--q3-neutral-default)]" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] bg-[var(--q3-surface-default)] border-[var(--q3-stroke-normal)] px-0 backdrop-blur-sm">
                <DialogHeader className="px-4">
                  <DialogTitle className="text-[var(--q3-neutral-default)]">Practice Mode Settings</DialogTitle>
                </DialogHeader>
                <hr />
                <div className="py-4 px-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-[var(--q3-neutral-default)]">Auto-start Timer</p>
                      <p className="text-sm text-[var(--q3-neutral-light)]">
                        Start timer automatically when opening a question
                      </p>
                    </div>
                    <button
                      onClick={() => handleAutoStartToggle(!autoStartTimer)}
                      className={`relative cursor-pointer inline-flex items-center h-6 rounded-full w-11 transition-colors ${autoStartTimer ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-surface-dimmest)]'}`}
                    >
                      <span
                        className={`inline-block w-4 h-4 transform bg-[var(--q3-neutral-default_inverted)] rounded-full transition-transform ${autoStartTimer ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button
              onClick={onClose}
              size="icon"
              className="inline-flex lg:hidden rounded-full w-8 h-8 bg-transparent hover:bg-[var(--q3-surface-dim)] items-center justify-center"
            >
              <X size={24} className="text-[var(--q3-neutral-default)]" />
            </Button>
          </div>
        </div>

        <div className="flex-grow p-6 overflow-y-auto">
          {currentQuestion.note && (
            <div className="flex flex-row items-center gap-2 mb-6 bg-[var(--q3-surface-dim)] p-2 rounded-lg">
              <p className="text-xs text-[var(--q3-neutral-light)] whitespace-nowrap">My Note:</p>
              <p className="text-sm text-[var(--q3-neutral-default)] truncate flex-1">{currentQuestion.note}</p>
            </div>
          )}
          <div className="flex items-center flex-wrap gap-4 mb-4">
            {tagDetail?.title && tagDetail?.title?.toLowerCase() !== 'not tagged' && (
              <Badge variant="outline" className="text-xs uppercase font-semibold flex items-center gap-1.5">
                {tagDetail.icon && <img src={tagDetail.icon} alt={tagDetail.title} className="w-4 h-4" />}
                {tagDetail.title}
              </Badge>
            )}
            {tagDetail?.title && <div className="h-5 w-[1px] bg-[var(--q3-stroke-normal)]" />}
            <Badge variant="outline" className="text-xs uppercase font-semibold" style={difficultyStyle}>
              {currentQuestion?.level && QUESTION_DIFFICULTY[currentQuestion.level]}
            </Badge>
            {currentQuestion.status && <div className="h-5 w-[1px] bg-[var(--q3-stroke-normal)]" />}
            <Badge
              variant="outline"
              className="text-xs uppercase font-semibold"
              style={{ color: statusStyle.color, borderColor: statusStyle.borderColor }}
            >
              {currentQuestion.status ? currentQuestion.status : 'Not Tagged'}
            </Badge>
            {currentQuestion?.attemptOverview && <div className="h-5 w-[1px] bg-[var(--q3-stroke-normal)]" />}
            {currentQuestion?.attemptOverview && (
              <div>
                <Badge
                  variant="outline"
                  className="text-xs uppercase font-semibold"
                  style={{ color: statusStyle.color, borderColor: statusStyle.borderColor }}
                >
                  {currentQuestion?.attemptOverview}
                </Badge>
              </div>
            )}
          </div>
          <div className="my-6 image-invert-wrapper">
            <Latex className="text-[var(--q3-neutral-default)] text-base leading-relaxed">{questionText}</Latex>
          </div>

          <div className="mb-6">
            {currentQuestion.type === 'numerical' ? (
              <div className="w-full">
                {!isAnswered ? (
                  <NumericalInput value={inputValue} onChange={setInputValue} />
                ) : (
                  getNumericalOption(currentSolution)
                )}
              </div>
            ) : (
              <div className="relative">
                {/* Only show optionsAfter when isAnswered is true */}
                {isAnswered && (
                  <div className="transition-opacity duration-300 opacity-100 image-invert-wrapper">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{optionsAfter}</div>
                  </div>
                )}
                {/* Only show optionsBefore when isAnswered is false */}
                {!isAnswered && (
                  <div className="transition-opacity duration-300 opacity-100 image-invert-wrapper">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{optionsBefore}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div ref={solutionRef}>
            {showSolution && (
              <div className="mb-6 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-[var(--q3-neutral-light)] mb-3">TEXT SOLUTION</h3>
                  <div className="bg-[var(--q3-surface-dim)] p-4 rounded-lg image-invert-wrapper">
                    {currentQuestion?.solution?.text || currentQuestion.rawData?.question?.solution?.text ? (
                      <Latex className="text-sm text-[var(--q3-neutral-default)] leading-relaxed">
                        {currentQuestion?.solution?.text || currentQuestion?.rawData?.question?.solution?.text}
                      </Latex>
                    ) : (
                      <p className="text-sm text-[var(--q3-neutral-light)] italic">No text solution available.</p>
                    )}
                  </div>
                </div>
                {(currentQuestion.videoSolution || currentQuestion?.rawData?.question?.videoSolution) && (
                  <VideoCipherPlayer
                    videoId={currentQuestion?.videoSolution || currentQuestion?.rawData?.question?.videoSolution}
                    isSolution
                    testId={testId || currentQuestion.testId}
                    questionId={
                      currentQuestion?.questionid ||
                      currentQuestion?.questionId ||
                      currentQuestion?.rawData?.question?._id
                    }
                    testTitle={currentQuestion?.rawData?.testTitle}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-[var(--q3-stroke-normal)] p-4 bg-[var(--q3-surface-default)]">
          <div className="flex items-center w-full gap-3">
            <Button
              onClick={handlePrevious}
              variant="outline"
              disabled={currentQuestionIndex === 0}
              className="flex-1 rounded-full bg-transparent"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>

            {!isAnswered && (
              <Button
                onClick={handleCheckAnswer}
                disabled={
                  (currentQuestion.type === 'numerical' && !inputValue) ||
                  (currentQuestion.type !== 'numerical' && !selectedOptions?.length)
                }
                className="bg-[var(--q3-accent-normal)] rounded-full px-8 flex-1"
              >
                Check Answer
              </Button>
            )}

            {isAnswered && !showSolution && (
              <Button onClick={handleViewSolution} className="bg-[var(--q3-accent-normal)] rounded-full px-8 flex-1">
                View solution
              </Button>
            )}

            {showSolution && <div className="flex-1" />}

            <Button
              onClick={handleNext}
              variant="outline"
              disabled={currentQuestionIndex === questions.length - 1}
              className="flex-1 rounded-full bg-transparent"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const NumericalInput = ({ value, onChange }) => {
  return (
    <div className="w-full flex flex-col md:flex-row gap-4 mb-4">
      <div className="flex-1 bg-[var(--q3-surface-dim)] rounded-lg">
        <div className="px-4 py-3 flex flex-col justify-center h-full">
          <div className="flex items-center justify-between px-2">
            <div className="flex flex-col gap-y-1 text-base font-medium text-[var(--q3-neutral-default)] w-full">
              <input
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter your answer"
                className="w-full rounded-lg border bg-[var(--q3-surface-default)] px-3 py-2 text-base text-[var(--q3-neutral-default)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)]"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 bg-[var(--q3-surface-default)]"></div>
    </div>
  );
};
